import "@fontsource-variable/instrument-sans";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "./styles.css";

import { ErrorState, Toaster, TooltipProvider } from "@forge/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initAnalytics } from "./lib/analytics";
import { initSentry } from "./lib/sentry";
import { routeTree } from "./routeTree.gen";

initSentry();
initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Without this a render error is a blank screen: the user sees nothing and we
  // hear nothing.
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorState
      title="Algo quebrou nesta tela"
      description="Tente de novo. Se continuar, avise o suporte com o horário."
      error={error}
      onRetry={reset}
    />
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    /**
     * `wide` lifts the reading-width cap of the app shell. Use it on dense
     * grids, never on forms or prose — those get harder to read, not easier.
     */
    layout?: "wide";
  }
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster>
          <RouterProvider router={router} />
        </Toaster>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
